import { useModal, useResourceNameModal } from '@hooks/useModal'
import { toast } from '@lib/toasts'
import { isAxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import {
  useCreateProjectTask,
  useDeleteProjectTask,
  useUpdateProjectTask,
} from './useProjectTasks'

const isNameTaken = (error: unknown) =>
  isAxiosError(error) && error.response?.status === 409

export const useManageProjectTasks = (project: AdminProject) => {
  const { t } = useTranslation()
  const modal = useModal()
  const { openResourceNameModal } = useResourceNameModal()
  const createTask = useCreateProjectTask(project.id)
  const updateTask = useUpdateProjectTask(project.id)
  const deleteTask = useDeleteProjectTask(project.id)

  const taskNameTaken = (name: string) =>
    t('features.projectsAdmin.projectDetails.taskNameTaken', {
      name: name.trim(),
      projectName: project.name,
    })

  const addTask = () => {
    openResourceNameModal({
      title: t('features.projectsAdmin.projectDetails.addTask'),
      inputLabel: t('features.projectsAdmin.projectDetails.taskName'),
      onSave: async (name: string) => {
        await createTask.mutateAsync({ name })
      },
      toastMessages: {
        success: {
          title: t('features.projectsAdmin.projectDetails.taskCreated'),
          content: (name) =>
            t('features.projectsAdmin.projectDetails.successfullyCreatedTask', {
              name: name.trim(),
              projectName: project.name,
            }),
        },
        error: {
          title: t('features.projectsAdmin.projectDetails.unableToCreateTask'),
          content: (name, error) =>
            isNameTaken(error)
              ? taskNameTaken(name)
              : t('features.projectsAdmin.projectDetails.failedToCreateTask'),
        },
      },
    })
  }

  const renameTask = (task: AdminProjectTask) => {
    openResourceNameModal({
      title: t('features.projectsAdmin.projectDetails.renameTask'),
      inputLabel: t('features.projectsAdmin.projectDetails.taskName'),
      initialValue: task.name,
      onSave: async (name: string) => {
        await updateTask.mutateAsync({ id: task.id, name })
      },
      toastMessages: {
        success: {
          title: t('features.projectsAdmin.projectDetails.taskSaved'),
          content: (name) =>
            t('features.projectsAdmin.projectDetails.successfullySavedTask', {
              name: name.trim(),
            }),
        },
        error: {
          title: t('features.projectsAdmin.projectDetails.unableToSaveTask'),
          content: (name, error) =>
            isNameTaken(error)
              ? taskNameTaken(name)
              : t('features.projectsAdmin.projectDetails.failedToSaveTask', {
                  name: task.name,
                }),
        },
      },
    })
  }

  const removeTask = (task: AdminProjectTask) => {
    modal.confirm({
      title: t('features.projectsAdmin.projectDetails.deleteTask'),
      content: (
        <>
          <p className="mt-2 mb-4 text-xl font-semibold">{task.name}</p>
          <p>
            {t('features.projectsAdmin.projectDetails.confirmDeleteTask', {
              projectName: project.name,
            })}
          </p>
        </>
      ),
      onConfirmAsync: async () => {
        try {
          await deleteTask.mutateAsync({ id: task.id })

          toast({
            title: t('features.projectsAdmin.projectDetails.taskDeleted'),
            variant: 'success',
            content: t(
              'features.projectsAdmin.projectDetails.successfullyDeletedTask',
              { name: task.name, projectName: project.name },
            ),
          })
        } catch {
          toast({
            title: t(
              'features.projectsAdmin.projectDetails.unableToDeleteTask',
            ),
            variant: 'error',
            content: t(
              'features.projectsAdmin.projectDetails.failedToDeleteTask',
              { name: task.name },
            ),
          })
        }
      },
      confirmText: t('common.delete'),
      primaryButtonClassName: 'bg-error-500 hover:bg-error-700',
    })
  }

  return { addTask, renameTask, removeTask }
}
